/**
 * Models → View model (Pedro 2026-11-01: "a View Model tab under Models that does for those what View
 * Portfolio does for Portfolios … Models use the same classifications as Portfolios and multiple
 * models can be selected to be shown side by side, but the only columns they show are % of total for
 * each row").
 *
 * Choose one or more models, a classification view and the grouping (as View portfolio: the same
 * lenses, tiers, sort orders, Cash and Non-Classified groups); each model is one column, its
 * effective weight per security / cash line (% of the model; "-" when not in it), each group's row
 * its sum, the Total 100 %. Read-only; `portfolios.read`. A line no longer Approved shows red.
 *
 * MV-LIKE (Pedro 2026-11-02, smoke 20–24: "can it remember the Grouping, as View Portfolio does?" ·
 * "Can the model picker be exactly like the Portfolio picker in View Portfolio, with all its
 * features?" · "Can we have a fold all and expand all? Please try to make this Model view as like
 * the Portfolio view as possible."): the picker is `ModelMultiPicker` (PortfolioPicker's shape); the
 * grouping is remembered on this computer (`models.groupLevels`, as `portfolios.groupLevels`); Fold
 * all / Unfold all as View portfolio; a summary line names the models shown; the heading stays in view.
 */
import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Alert, Button, Group, MultiSelect, Select, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconChevronRight, IconFileSpreadsheet } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { classificationApi } from '../api/classification';
import { modelsApi, type ModelViewRow, type ModelViewXlsxRow } from '../api/models';
import { ColumnResizeHandle } from '../components/ColumnResizeHandle';
import { ColumnsPicker } from '../components/ColumnsPicker';
import { ordered, useColumnOrder, useColumnWidths, useHeadingDrag } from '../components/gridColumnOrder';
import { ModelMultiPicker } from '../components/ModelMultiPicker';
import {
  allGroupIds, groupRowBg, groupRows, levelKey, levelOptions, validLevels, type GroupNode, type RowAccess,
} from '../utils/portfolioGrouping';

const readLocal = <T,>(key: string, fallback: T): T => {
  try { const v = localStorage.getItem(key); return v == null ? fallback : (JSON.parse(v) as T); }
  catch { return fallback; }
};

const ACCESS: RowAccess<ModelViewRow> = {
  path: (r, code) => r.classifications[code],
  orders: (r, code) => r.orders?.[code] ?? [],
  special: (r) => (r.is_cash ? 'cash' : r.non_classified ? 'non' : null),
  value: () => 0,
};
const pct = (v: number | null) => (v == null ? '-' : `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
const rowsOf = (g: GroupNode<ModelViewRow>): ModelViewRow[] => (g.children.length ? g.children.flatMap(rowsOf) : g.lines);
const INDENT = 16;
/** A column of the grid: identity text, or one model's % (`modelId`). */
interface Col { key: string; label: string; group: string; width: number; modelId?: string }

export function ModelViewTab() {
  const modelsQ = useQuery({ queryKey: ['models', 'list'], queryFn: () => modelsApi.models() });
  const viewsQ = useQuery({ queryKey: ['classification', 'schemes'], queryFn: () => classificationApi.schemes() });
  const [ids, setIds] = useState<string[]>([]);
  const [schemeId, setSchemeId] = useState<string | null>(null);
  // null = the default (the first lens's top tier); remembered on this computer, as View portfolio does.
  const [levelKeys, setLevelKeys] = useState<string[] | null>(() => readLocal<string[] | null>('models.groupLevels', null));
  useEffect(() => { try { localStorage.setItem('models.groupLevels', JSON.stringify(levelKeys)); } catch { /* private window */ } }, [levelKeys]);
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const q = useQuery({ queryKey: ['models', 'view', ids, schemeId], queryFn: () => modelsApi.view(ids, schemeId), enabled: ids.length > 0 });
  const v = q.data;
  const lenses = useMemo(() => v?.lenses ?? [], [v]);
  // As View portfolio: grouped by the first lens's top tier until the user chooses.
  const levels = validLevels(levelKeys ?? (lenses[0] ? [levelKey({ code: lenses[0].code, tier: 1 })] : []), lenses);
  const { groups, ungrouped } = useMemo(() => groupRows(v?.rows ?? [], levels, lenses, ACCESS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [v, levels.map(levelKey).join(','), lenses]);
  const models = v?.models ?? [];
  const groupIds = allGroupIds(groups);
  const sumOf = (rs: ModelViewRow[], id: string) => rs.reduce((s, r) => s + (r.by_model[id] ? Number(r.by_model[id]) : 0), 0);
  const viewOptions = (viewsQ.data ?? []).map((s) => ({ value: s.id, label: s.is_active ? `${s.name} (active)` : s.name }));
  const activeId = (viewsQ.data ?? []).find((s) => s.is_active)?.id ?? null;
  // ── MV-COLS (Pedro 2026-11-02: "please include BB ticker and FIGI in the on screen and download views
  // and a column picker that allows to show or hide, a freeze to, and width setter"): View portfolio's
  // grid controls — the Columns picker (show / hide, drag to re-order), "Freeze columns through", a
  // heading's right edge dragged to set its width, a heading dragged to move it; all remembered on
  // this computer (`models.*`, as `portfolios.*`). The export follows what is shown.
  const base: Col[] = useMemo(() => [
    { key: 'name', label: 'Security', group: 'Identity', width: 320 },
    { key: 'isin', label: 'ISIN', group: 'Identity', width: 130 },
    { key: 'bb_ticker', label: 'BB Ticker', group: 'Identity', width: 160 },
    { key: 'figi', label: 'FIGI', group: 'Identity', width: 140 },
    { key: 'ccy', label: 'Ccy', group: 'Identity', width: 60 },
    ...models.map((m) => ({ key: `m:${m.id}`, label: `${m.name} (% of total)`, group: 'Models', width: 170, modelId: m.id })),
  ], [models]);
  const colOrder = useColumnOrder('models.columnOrder', base.map((b) => b.key));
  const orderedBase = ordered(base, colOrder.order);
  const colWidths = useColumnWidths('models.columnWidths');
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(readLocal<string[]>('models.hiddenCols', [])));
  useEffect(() => { try { localStorage.setItem('models.hiddenCols', JSON.stringify([...hidden])); } catch { /* private window */ } }, [hidden]);
  const [freezeKey, setFreezeKey] = useState<string>(() => readLocal<string>('models.freezeKey', 'name'));
  useEffect(() => { try { localStorage.setItem('models.freezeKey', JSON.stringify(freezeKey)); } catch { /* private window */ } }, [freezeKey]);
  const visible = orderedBase.filter((c) => !hidden.has(c.key)).map((c) => ({ ...c, width: colWidths.widthOf(c.key, c.width) }));
  const freezeIdx = visible.findIndex((x) => x.key === freezeKey);
  const lefts = visible.reduce<number[]>((acc, _x, i) => [...acc, i === 0 ? 0 : acc[i - 1] + visible[i - 1].width], []);
  const drag = useHeadingDrag(colOrder.place);
  const toggleCol = (k: string) => setHidden((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  /** A line's cell for a column: text, or a model's % ("-" when not in that model). */
  const lineValue = (r: ModelViewRow, c: Col): string | null => {
    if (c.modelId) return r.by_model[c.modelId] ? r.by_model[c.modelId] : null;
    if (c.key === 'name') return r.name ?? '-';
    if (c.key === 'ccy') return r.currency;
    return (r[c.key as 'isin' | 'bb_ticker' | 'figi'] as string | null) ?? null;
  };

  // Export to Excel (Pedro 2026-11-02, "yes for Excel"): the table as shown — the columns shown, in
  // their order and widths, frozen as on screen — every group open (whatever is folded on screen),
  // group rows with their sums and their label in the first column, the Total.
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    if (!v || !visible.length) return;
    const fix = (x: number) => x.toFixed(6);
    const out: ModelViewXlsxRow[] = [];
    const labelled = (label: string, rest: (c: Col) => string | null) => visible.map((c, i) => (i === 0 ? label : rest(c)));
    const lineRow = (r: ModelViewRow, depth: number) => out.push({ kind: 'line', depth, cells: visible.map((c) => lineValue(r, c)) });
    const walk = (g: GroupNode<ModelViewRow>) => {
      const rs = rowsOf(g);
      out.push({ kind: 'group', depth: g.depth,
        cells: labelled(`${g.label} (${rs.length})`, (c) => (c.modelId ? fix(sumOf(rs, c.modelId)) : null)) });
      if (g.children.length) g.children.forEach(walk); else g.lines.forEach((r) => lineRow(r, g.depth + 1));
    };
    groups.forEach(walk);
    ungrouped.forEach((r) => lineRow(r, 0));
    out.push({ kind: 'total', depth: 0,
      cells: labelled('Total', (c) => (c.modelId ? models.find((m) => m.id === c.modelId)?.total_pct ?? null : null)) });
    const groupLabels = Object.fromEntries(levelOptions(lenses).flatMap((g) => g.items).map((i) => [i.value, i.label]));
    setExporting(true);
    try {
      await modelsApi.downloadView({ view: v.view?.name ?? null,
        grouping: levels.map((l) => groupLabels[levelKey(l)] ?? levelKey(l)).join(' › ') || null,
        models: models.map((m) => m.name),
        columns: visible.map((c) => ({ label: c.label, pct: !!c.modelId, width: c.width })),
        freeze: freezeIdx + 1, rows: out });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Export failed', message: e instanceof Error ? e.message : 'The Excel file could not be generated.' });
    } finally { setExporting(false); }
  };
  const toggle = (id: string) => setFolded((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x; });

  // As View portfolio's grid: fixed widths, the frozen columns sticky on the left, the heading sticky on top.
  const cellStyle = (vi: number, bg: string, isHead: boolean, bold = false): React.CSSProperties => {
    const x = visible[vi];
    const frozen = vi <= freezeIdx;
    return {
      width: x.width, minWidth: x.width, maxWidth: x.width, padding: '4px 10px', boxSizing: 'border-box',
      background: bg, borderBottom: '1px solid var(--mantine-color-gray-3)',
      borderRight: vi === freezeIdx ? '2px solid var(--mantine-color-gray-4)' : '1px solid var(--mantine-color-gray-2)',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, fontWeight: bold ? 600 : undefined,
      textAlign: x.modelId ? 'right' : 'left',
      position: (isHead || frozen) ? 'sticky' : undefined, top: isHead ? 0 : undefined,
      left: frozen ? lefts[vi] : undefined, zIndex: isHead && frozen ? 4 : isHead ? 3 : frozen ? 2 : undefined,
    };
  };
  const line = (r: ModelViewRow, depth: number) => (
    <tr key={r.key}>
      {visible.map((c, vi) => {
        const val = lineValue(r, c);
        const st = cellStyle(vi, 'var(--mantine-color-body)', false);
        if (vi === 0) st.paddingLeft = 10 + depth * INDENT;
        return (
          <td key={c.key} style={st} title={c.modelId ? undefined : val ?? undefined}>
            {c.key === 'name' && r.flag
              ? <Tooltip withArrow label={r.flag}><Text span size="sm" c="red.7">{val}</Text></Tooltip>
              : c.modelId ? (val == null ? '-' : pct(Number(val))) : (val ?? '')}
          </td>
        );
      })}
    </tr>
  );
  const group = (g: GroupNode<ModelViewRow>): React.ReactNode[] => {
    const rs = rowsOf(g);
    const open = !folded.has(g.id);
    return [
      <tr key={`g:${g.id}`} style={{ cursor: 'pointer' }} onClick={() => toggle(g.id)}>
        {visible.map((c, vi) => {
          const st = cellStyle(vi, groupRowBg(g.depth), false, true);
          if (vi === 0) st.paddingLeft = 6 + g.depth * INDENT;
          return (
            <td key={c.key} style={st} title={vi === 0 ? `${g.label} (${rs.length})` : undefined}>
              {vi === 0 ? (
                <Group gap={4} wrap="nowrap">
                  <ActionIcon size="xs" variant="subtle" color="gray" aria-label={open ? 'Fold' : 'Unfold'}>
                    {open ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
                  </ActionIcon>
                  <Text size="sm" fw={600}>{g.label}</Text>
                  <Text size="xs" c="dimmed">({rs.length})</Text>
                </Group>
              ) : c.modelId ? pct(sumOf(rs, c.modelId)) : null}
            </td>
          );
        })}
      </tr>,
      ...(open ? (g.children.length ? g.children.flatMap(group) : g.lines.map((r) => line(r, g.depth + 1))) : []),
    ];
  };

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <ModelMultiPicker models={modelsQ.data?.items ?? []} value={ids} onChange={(x) => { setIds(x); setFolded(new Set()); }} />
        <Select size="xs" label="Classification view" w={190} allowDeselect={false} data={viewOptions}
          value={schemeId ?? v?.view?.id ?? activeId} onChange={(id) => { setSchemeId(id); setFolded(new Set()); }}
          disabled={viewOptions.length === 0} />
        {/* Room for three levels on one line (smoke 2026-11-02: "allow some more room for the Grouping picker") */}
        <MultiSelect size="xs" label="Group by (in order)" w={480} clearable placeholder={levels.length ? undefined : 'No grouping'}
          data={levelOptions(lenses)} value={levels.map(levelKey)} onChange={(x) => { setLevelKeys(x); setFolded(new Set()); }}
          disabled={!v} />
        <Select size="xs" label="Freeze columns through" w={170} allowDeselect={false}
          data={[{ value: '__none__', label: 'None' }, ...visible.map((x) => ({ value: x.key, label: x.label }))]}
          value={freezeIdx >= 0 ? freezeKey : '__none__'} onChange={(k) => setFreezeKey(k ?? '__none__')} />
        <ColumnsPicker size="xs" columns={orderedBase.map((b) => ({ key: b.key, label: b.label, group: b.group }))}
          hidden={hidden} onToggle={toggleCol} onShowAll={() => setHidden(new Set())}
          onPlace={colOrder.place} onResetOrder={() => { colOrder.reset(); colWidths.reset(); }}
          customOrder={colOrder.custom || colWidths.custom} />
        {groupIds.length > 0 && (
          <Button size="xs" variant="subtle" onClick={() => setFolded(folded.size ? new Set() : new Set(groupIds))}>
            {folded.size ? 'Unfold all' : 'Fold all'}
          </Button>
        )}
        <Tooltip withArrow multiline w={300} label={v ? 'The table as shown, every group open: the models side by side, group sums and the Total, as % numbers.' : 'Choose models first.'}>
          <Button size="xs" variant="light" ml="auto" leftSection={<IconFileSpreadsheet size={14} />} disabled={!v} loading={exporting}
            onClick={() => void exportXlsx()}>Export to Excel</Button>
        </Tooltip>
      </Group>
      {ids.length === 0 && <Text size="sm" c="dimmed">Choose one or more models to see them side by side: each security's and each currency's cash weight, as % of each model, grouped as in View portfolio.</Text>}
      {q.error && <Alert color="red">{q.error instanceof Error ? q.error.message : 'The models could not be read.'}</Alert>}
      {/* As View portfolio's line above its table: what is shown. */}
      {v && (
        <Text size="sm">
          <b>{models.length === 1 ? '1 model' : `${models.length} models`}</b>{' '}
          <Text span c="dimmed">{models.map((m) => m.name).join(' · ')}</Text>
          {' · '}{v.rows.length} line{v.rows.length === 1 ? '' : 's'}
        </Text>
      )}
      {v && visible.length === 0 && <Text size="sm" c="dimmed">Every column is hidden: open Columns and tick some.</Text>}
      {v && visible.length > 0 && (
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)', border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: visible.reduce((s, x) => s + x.width, 0) }}>
            <thead>
              <tr>
                {visible.map((c, vi) => (
                  <th key={c.key} {...drag.props(c.key)} title={c.modelId ? "% of the model's total" : c.label}
                    style={{ ...cellStyle(vi, 'var(--mantine-color-gray-1)', true, true), whiteSpace: 'normal', verticalAlign: 'bottom', ...drag.marker(c.key) }}>
                    {c.label}
                    <ColumnResizeHandle width={c.width} onResize={(w) => colWidths.setWidth(c.key, w)} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.flatMap(group)}
              {ungrouped.map((r) => line(r, 0))}
              <tr>
                {visible.map((c, vi) => (
                  <td key={c.key} style={{ ...cellStyle(vi, 'var(--mantine-color-gray-2)', false, true), fontWeight: 700 }}>
                    {vi === 0 ? 'Total' : c.modelId ? pct(Number(models.find((m) => m.id === c.modelId)?.total_pct ?? 0)) : null}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Stack>
  );
}
